import Debug from 'debug';
import { Client } from 'bittorrent-tracker';
import { EventEmitter } from 'events';
import { Buffer } from 'buffer';
import sha1 from 'sha.js/sha1';
import { MediaPeer } from './media-peer';
const version = '0.6.2';
const PEER_PROTOCOL_VERSION = 2;
const PEER_ID_VERSION_STRING = version.replace(/\d*./g, (v) => `0${parseInt(v, 10) % 100}`.slice(-2)).slice(0, 4);
const PEER_ID_VERSION_PREFIX = `-WW${PEER_ID_VERSION_STRING}-`;
class PeerSegmentRequest {
    constructor(peerId, segment, callbacks) {
        this.peerId = peerId;
        this.segment = segment;
        this.callbacks = callbacks;
    }
}
function generatePeerId() {
    const PEER_ID_SYMBOLS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const PEER_ID_LENGTH = 20;
    let peerId = PEER_ID_VERSION_PREFIX;
    for (let i = 0; i < PEER_ID_LENGTH - PEER_ID_VERSION_PREFIX.length; i++) {
        peerId += PEER_ID_SYMBOLS.charAt(Math.floor(Math.random() * PEER_ID_SYMBOLS.length));
    }
    return new TextEncoder().encode(peerId).buffer;
}
export class P2PMediaManager extends EventEmitter {
    constructor(streamSwarmId, settings, segmentsStorage) {
        super();
        this.streamSwarmId = streamSwarmId;
        this.settings = settings;
        this.segmentsStorage = segmentsStorage;
        this.trackerClient = null;
        this.peers = new Map();
        this.peerCandidates = new Map();
        this.peerSegmentRequests = new Map();
        this.debug = Debug('p2pml:p2p-media-manager');
        this.peersCheckTimes = 0;
        this.getPeers = () => {
            return this.peers;
        };
        this.getPeerId = () => {
            return Buffer.from(this.peerId).toString('hex');
        };
        this.createClient = () => {
            const infoHash = new sha1().update(`${PEER_PROTOCOL_VERSION}${this.streamSwarmId}`).digest();
            const clientOptions = {
                infoHash: Buffer.from(infoHash, 0, 20),
                peerId: Buffer.from(this.peerId, 0, 20),
                announce: this.settings.trackerAnnounce,
                rtcConfig: this.settings.rtcConfig,
                port: 6881,
                getAnnounceOpts: () => {
                    return { numwant: this.settings.peerRequestsPerAnnounce };
                },
            };
            let oldTrackerClient = this.trackerClient;
            this.trackerClient = new Client(clientOptions);
            this.trackerClient.on('error', this.onTrackerError);
            this.trackerClient.on('warning', this.onTrackerWarning);
            this.trackerClient.on('update', this.onTrackerUpdate);
            this.trackerClient.on('peer', this.onTrackerPeer);
            this.trackerClient.start();
            if (oldTrackerClient !== null) {
                oldTrackerClient.destroy();
                oldTrackerClient = null;
            }
        };
        this.onTrackerError = (error) => {
            this.debug('tracker error', error);
        };
        this.onTrackerWarning = (warning) => {
            this.debug('tracker warning', warning);
        };
        this.onTrackerUpdate = (data) => {
            this.debug('tracker update', data);
            this.emit('tracker-update', data);
        };
        this.onTrackerPeer = (trackerPeer) => {
            this.debug('tracker peer', trackerPeer.id, trackerPeer);
            if (this.peers.has(trackerPeer.id)) {
                this.debug('tracker peer already connected', trackerPeer.id, trackerPeer);
                trackerPeer.destroy();
                return;
            }
            const peer = new MediaPeer(trackerPeer, this.settings);
            peer.on('connect', this.onPeerConnect);
            peer.on('close', this.onPeerClose);
            peer.on('data-updated', this.onPeerDataUpdated);
            peer.on('segment-request', this.onSegmentRequest);
            peer.on('segment-loaded', this.onSegmentLoaded);
            peer.on('segment-absent', this.onSegmentAbsent);
            peer.on('segment-error', this.onSegmentError);
            peer.on('segment-timeout', this.onSegmentTimeout);
            peer.on('bytes-downloaded', this.onPieceBytesDownloaded);
            peer.on('bytes-uploaded', this.onPieceBytesUploaded);
            let peerCandidatesById = this.peerCandidates.get(peer.id);
            if (!peerCandidatesById) {
                peerCandidatesById = [];
                this.peerCandidates.set(peer.id, peerCandidatesById);
            }
            peerCandidatesById.push(peer);
        };
        this.checkPendingTimeout = (segment) => {
            const canP2P = this.canDownload(segment);
            if (canP2P)
                return 0;
            const timeoutList = Array.from(this.peers.values())
                .map((peer) => peer.checkPendingTimeout(segment.id))
                .filter((timeout) => timeout > 0);
            const timeout = Math.max(...timeoutList);
            return timeout > 0 ? timeout + 100 : 0;
        };
        this.canDownload = (segment) => {
            if (Array.from(this.peers.keys()).length === 0) {
                if (this.peersCheckTimes > 20) {
                    this.peersCheckTimes = 0;
                    this.debug('没有可用的 Peer, 重新创建 TrackerClient');
                    this.createClient();
                }
                else {
                    this.peersCheckTimes++;
                }
                return false;
            }
            const candidates = [];
            for (const peer of Array.from(this.peers.values())) {
                if (peer.canDownload(segment.id)) {
                    candidates.push(peer);
                }
            }
            if (candidates.length === 0) {
                return false;
            }
            return true;
        };
        this.load = (segment, callbacks) => {
            const candidates = [];
            for (const peer of Array.from(this.peers.values())) {
                if (peer.canDownload(segment.id)) {
                    candidates.push(peer);
                }
            }
            if (candidates.length === 0) {
                callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError(null, { message: 'no_peer_found' });
                return false;
            }
            const peer = candidates[Math.floor(Math.random() * candidates.length)];
            peer.requestSegment(segment.id);
            this.debug('使用 p2p 下载', peer.id, segment.id);
            this.peerSegmentRequests.set(segment.id, new PeerSegmentRequest(peer.id, segment, callbacks));
            return true;
        };
        this.abort = (segment) => {
            let downloadingSegment;
            const peerSegmentRequest = this.peerSegmentRequests.get(segment.id);
            if (peerSegmentRequest) {
                const peer = this.peers.get(peerSegmentRequest.peerId);
                if (peer) {
                    downloadingSegment = peer.cancelSegmentRequest();
                }
                this.peerSegmentRequests.delete(segment.id);
            }
            return downloadingSegment;
        };
        this.isDownloading = (segment) => {
            return this.peerSegmentRequests.has(segment.id);
        };
        this.getActiveDownloadsCount = () => {
            return this.peerSegmentRequests.size;
        };
        this.destroy = (swarmChange = false) => {
            this.streamSwarmId = '';
            if (this.trackerClient) {
                this.trackerClient.stop();
                if (swarmChange) {
                    this.trackerClient.removeAllListeners('error');
                    this.trackerClient.removeAllListeners('warning');
                    this.trackerClient.removeAllListeners('update');
                    this.trackerClient.removeAllListeners('peer');
                }
                else {
                    this.trackerClient.destroy();
                    this.trackerClient = null;
                }
            }
            this.peers.forEach((peer) => peer.destroy());
            this.peers.clear();
            this.peerSegmentRequests.clear();
            for (const peerCandidateById of Array.from(this.peerCandidates.values())) {
                for (const peerCandidate of peerCandidateById) {
                    peerCandidate.destroy();
                }
            }
            this.peerCandidates.clear();
        };
        this.sendSegmentsMapToAll = (segmentsMap) => {
            this.debug('发送本地 storage 分片到对等端', Array.from(this.peers.keys()));
            this.peers.forEach((peer) => peer.sendSegmentsMap(segmentsMap));
        };
        this.sendSegmentsPendingToAll = (segmentsPending, delay) => {
            this.debug('发送本地 pending 分片到对等端', Array.from(this.peers.keys()));
            this.peers.forEach((peer) => peer.sendSegmentsPending(`${segmentsPending}|${delay}`));
        };
        this.sendSegmentsMap = (peerId, segmentsMap) => {
            const peer = this.peers.get(peerId);
            if (peer) {
                peer.sendSegmentsMap(segmentsMap);
            }
        };
        this.onPieceBytesDownloaded = (peer, segmentId, bytes) => {
            var _a, _b;
            this.emit('bytes-downloaded', bytes, peer.id);
            const peerSegmentRequest = this.peerSegmentRequests.get(segmentId);
            if (!peerSegmentRequest) {
                return;
            }
            if ((_a = peerSegmentRequest.callbacks) === null || _a === void 0 ? void 0 : _a.onPiece) {
                (_b = peerSegmentRequest.callbacks) === null || _b === void 0 ? void 0 : _b.onPiece(peer.id, bytes);
            }
        };
        this.onPieceBytesUploaded = (peer, bytes) => {
            this.emit('bytes-uploaded', bytes, peer.id);
        };
        this.onPeerConnect = (peer) => {
            const connectedPeer = this.peers.get(peer.id);
            if (connectedPeer) {
                this.debug('tracker peer already connected (in peer connect)', peer.id, peer);
                peer.destroy();
                return;
            }
            this.debug('添加新 peer 到 peers', peer.id, JSON.stringify(this.peers.keys(), null, 2));
            this.peers.set(peer.id, peer);
            const peerCandidatesById = this.peerCandidates.get(peer.id);
            if (peerCandidatesById) {
                for (const peerCandidate of peerCandidatesById) {
                    if (peerCandidate !== peer) {
                        peerCandidate.destroy();
                    }
                }
                this.peerCandidates.delete(peer.id);
            }
            this.emit('peer-connected', { id: peer.id, remoteAddress: peer.remoteAddress });
        };
        this.onPeerClose = (peer) => {
            var _a, _b;
            if (this.peers.get(peer.id) !== peer) {
                const peerCandidatesById = this.peerCandidates.get(peer.id);
                if (!peerCandidatesById) {
                    return;
                }
                const index = peerCandidatesById.indexOf(peer);
                if (index !== -1) {
                    peerCandidatesById.splice(index, 1);
                }
                if (peerCandidatesById.length === 0) {
                    this.peerCandidates.delete(peer.id);
                }
                return;
            }
            for (const [key, value] of Array.from(this.peerSegmentRequests)) {
                if (value.peerId === peer.id) {
                    if ((_a = value.callbacks) === null || _a === void 0 ? void 0 : _a.onError) {
                        (_b = value.callbacks) === null || _b === void 0 ? void 0 : _b.onError(peer.id, { message: 'peer_closed' });
                    }
                    this.peerSegmentRequests.delete(key);
                }
            }
            this.peers.delete(peer.id);
            this.emit('peer-data-updated');
            this.emit('peer-closed', peer.id);
        };
        this.onPeerDataUpdated = ({ peerId, segmentsMap }) => {
            this.emit('peer-segments-map-updated', { peerId, segmentsMap });
            this.emit('peer-data-updated');
        };
        this.onSegmentRequest = (peer, segmentId) => {
            const segment = this.segmentsStorage.getSegment(segmentId);
            if (segment && segment.data) {
                peer.sendSegmentData(segmentId, segment.data);
            }
            else {
                peer.sendSegmentAbsent(segmentId);
            }
        };
        this.onSegmentLoaded = async (peer, segmentId, data) => {
            var _a, _b;
            const peerSegmentRequest = this.peerSegmentRequests.get(segmentId);
            if (!peerSegmentRequest) {
                return;
            }
            const segment = peerSegmentRequest.segment;
            if ((_a = peerSegmentRequest.callbacks) === null || _a === void 0 ? void 0 : _a.onSuccess) {
                (_b = peerSegmentRequest.callbacks) === null || _b === void 0 ? void 0 : _b.onSuccess(peer.id, data.slice(0));
            }
            this.peerSegmentRequests.delete(segmentId);
            this.emit('segment-loaded', segment, data, peer.id);
        };
        this.onSegmentAbsent = (peer, segmentId) => {
            var _a, _b;
            const peerSegmentRequest = this.peerSegmentRequests.get(segmentId);
            if (peerSegmentRequest) {
                if ((_a = peerSegmentRequest.callbacks) === null || _a === void 0 ? void 0 : _a.onError) {
                    (_b = peerSegmentRequest.callbacks) === null || _b === void 0 ? void 0 : _b.onError(peer.id, { message: 'segment_absent' });
                }
                this.peerSegmentRequests.delete(segmentId);
                this.emit('peer-data-updated');
            }
        };
        this.onSegmentError = (peer, segmentId, description) => {
            var _a, _b;
            const peerSegmentRequest = this.peerSegmentRequests.get(segmentId);
            if (peerSegmentRequest) {
                if ((_a = peerSegmentRequest.callbacks) === null || _a === void 0 ? void 0 : _a.onError) {
                    (_b = peerSegmentRequest.callbacks) === null || _b === void 0 ? void 0 : _b.onError(peer.id, { message: 'segment_error:' + description });
                }
                this.peerSegmentRequests.delete(segmentId);
                this.emit('segment-error', peerSegmentRequest.segment, description, peer.id);
            }
        };
        this.onSegmentTimeout = (peer, segmentId) => {
            var _a, _b;
            const peerSegmentRequest = this.peerSegmentRequests.get(segmentId);
            if (peerSegmentRequest) {
                if ((_a = peerSegmentRequest.callbacks) === null || _a === void 0 ? void 0 : _a.onError) {
                    (_b = peerSegmentRequest.callbacks) === null || _b === void 0 ? void 0 : _b.onError(peer.id, { message: 'segment_timeout' });
                }
                this.peerSegmentRequests.delete(segmentId);
                peer.destroy();
                if (this.peers.delete(peerSegmentRequest.peerId)) {
                    this.emit('peer-data-updated');
                }
            }
        };
        this.peerId = generatePeerId();
        if (this.debug.enabled) {
            this.debug('peer ID', this.getPeerId(), new TextDecoder().decode(this.peerId));
        }
        this.debug('stream swarm ID', this.streamSwarmId);
        this.createClient();
    }
}
